<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  recurrenceRule: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:show', 'update:recurrenceRule']);

const { t } = useI18n();

const localRule = ref(props.recurrenceRule ? { ...props.recurrenceRule } : { frequency: 'daily', interval: 1, end_type: 'never' });
const activeTab = ref('frequency');

const tabs = [
  { id: 'frequency', label: t('RECURRENCE.INTERVAL') },
  { id: 'end', label: t('RECURRENCE.END') },
];

const frequencyOptions = [
  { value: 'daily', label: t('RECURRENCE.FREQUENCY.DAILY'), icon: 'sun' },
  { value: 'weekly', label: t('RECURRENCE.FREQUENCY.WEEKLY'), icon: 'calendar' },
  { value: 'monthly', label: t('RECURRENCE.FREQUENCY.MONTHLY'), icon: 'calendar-days' },
  { value: 'yearly', label: t('RECURRENCE.FREQUENCY.YEARLY'), icon: 'calendar-year' },
];

const endTypeOptions = [
  { value: 'never', label: t('RECURRENCE.END_NEVER') },
  { value: 'on_date', label: t('RECURRENCE.END_ON_DATE') },
  { value: 'after_count', label: t('RECURRENCE.END_AFTER_COUNT') },
];

const monthlyTypeOptions = [
  { value: 'day_of_month', label: t('RECURRENCE.MONTHLY_TYPE.DAY_OF_MONTH') },
  { value: 'day_of_week', label: t('RECURRENCE.MONTHLY_TYPE.DAY_OF_WEEK') },
];

const DAY_KEYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_KEYS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const shortDayName = index => t(`RECURRENCE.DAYS_SHORT.${DAY_KEYS[index] ?? 'SUN'}`);
const fullDayName = index => t(`RECURRENCE.DAY_NAMES_FULL.${DAY_KEYS[index] ?? 'SUN'}`);
const monthName = index => t(`RECURRENCE.MONTHS.${MONTH_KEYS[index] ?? 'JAN'}`);

const weekDays = DAY_KEYS.map((key, index) => ({
  value: index,
  label: shortDayName(index),
  full: fullDayName(index),
}));

watch(
  () => props.recurrenceRule,
  val => {
    if (val) {
      localRule.value = { ...val };
    }
  }
);

watch(
  () => props.show,
  val => {
    if (val && !props.recurrenceRule) {
      localRule.value = { frequency: 'daily', interval: 1, end_type: 'never' };
    }
  }
);

const save = () => {
  emit('update:recurrenceRule', { ...localRule.value });
  emit('update:show', false);
};

const close = () => {
  emit('update:show', false);
};

const setFrequency = value => {
  localRule.value.frequency = value;
  // Reset dependent fields
  if (value !== 'weekly') delete localRule.value.week_days;
  if (value !== 'monthly') {
    delete localRule.value.monthly_type;
    delete localRule.value.monthly_week;
    delete localRule.value.monthly_weekday;
    delete localRule.value.month_day;
  }
  if (value !== 'yearly') {
    delete localRule.value.year_month;
    delete localRule.value.year_day;
  }
  if (value === 'monthly' && !localRule.value.monthly_type) {
    localRule.value.monthly_type = 'day_of_month';
    localRule.value.month_day = 1;
  }
  if (value === 'yearly') {
    if (localRule.value.year_month == null) localRule.value.year_month = 1;
    if (localRule.value.year_day == null) localRule.value.year_day = 1;
  }
};

const setEndType = value => {
  localRule.value.end_type = value;
  if (value !== 'on_date') delete localRule.value.end_date;
  if (value !== 'after_count') delete localRule.value.end_count;
};

const setMonthlyType = value => {
  localRule.value.monthly_type = value;
  if (value !== 'day_of_week') {
    delete localRule.value.monthly_week;
    delete localRule.value.monthly_weekday;
  } else {
    delete localRule.value.month_day;
  }
};
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="fixed inset-0 bg-black/50" @click="close" />
        <div class="relative w-full max-w-2xl bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.05)] dark:bg-[#252535] border border-black/5 dark:border-white/10">
          <!-- Header -->
          <div class="flex items-center justify-between border-b border-n-divider dark:border-white/10 px-6 py-4">
            <h2 class="text-lg font-semibold text-n-text dark:text-n-text-white">
              {{ t('RECURRENCE.CUSTOM_TITLE') }}
            </h2>
            <button
              type="button"
              class="text-n-text-light hover:text-n-text dark:hover:text-n-text-white transition-colors"
              @click="close"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Tabs -->
          <div class="flex border-b border-n-divider dark:border-white/10">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              class="flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors"
              :class="{
                'border-n-primary-600 text-n-primary-600 dark:text-n-primary-400': activeTab === tab.id,
                'text-n-text-light dark:text-n-text-muted': activeTab !== tab.id,
              }"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Tab Panels -->
          <div class="p-6 space-y-6">
            <!-- Frequency Tab -->
            <div v-if="activeTab === 'frequency'" class="space-y-6">
              <div>
                <label class="block text-sm font-medium text-n-text dark:text-n-text-white mb-3">
                  {{ t('RECURRENCE.INTERVAL') }}
                </label>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <button
                    v-for="opt in frequencyOptions"
                    :key="opt.value"
                    type="button"
                    class="p-4 border-2 rounded-lg text-center transition-all"
                    :class="{
                      'border-n-primary-500 bg-n-primary-50 dark:bg-n-primary-900/20 text-n-primary-700 dark:text-n-primary-300':
                        localRule.frequency === opt.value,
                      'border-n-divider dark:border-n-divider text-n-text dark:text-n-text-white hover:border-n-primary-300':
                        localRule.frequency !== opt.value,
                    }"
                    @click="setFrequency(opt.value)"
                  >
                    <template v-if="opt.icon === 'sun'">
                      <svg class="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </template>
                    <template v-else-if="opt.icon === 'calendar'">
                      <svg class="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </template>
                    <template v-else>
                      <svg class="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </template>
                    <div class="font-medium">{{ opt.label }}</div>
                  </button>
                </div>
              </div>

              <div v-if="localRule.frequency">
                <label class="block text-sm font-medium text-n-text dark:text-n-text-white mb-2">
                  {{ t('RECURRENCE.INTERVAL') }}
                </label>
                <div class="flex items-center gap-4">
                  <input
                    type="number"
                    min="1"
                    v-model.number="localRule.interval"
                    class="w-24 px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
                  />
                  <span class="text-n-text-light dark:text-n-text-muted">
                    {{ t('RECURRENCE.EVERY_N', { n: localRule.interval }) }}
                  </span>
                </div>
              </div>

              <!-- Weekly options -->
              <div v-if="localRule.frequency === 'weekly'" class="space-y-4">
                <label class="block text-sm font-medium text-n-text dark:text-n-text-white">
                  {{ t('RECURRENCE.WEEK_DAYS') }}
                </label>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="day in weekDays"
                    :key="day.value"
                    type="button"
                    class="px-4 py-2 text-sm rounded-lg border transition-colors"
                    :class="{
                      'bg-n-primary-100 border-n-primary-300 text-n-primary-700 dark:bg-n-primary-900/30 dark:border-n-primary-700 dark:text-n-primary-300':
                        localRule.week_days?.includes(day.value),
                      'bg-white border-n-divider text-n-text dark:bg-n-surface-1 dark:border-n-divider dark:text-n-text-white':
                        !localRule.week_days?.includes(day.value),
                    }"
                    @click="() => {
                      const days = [...(localRule.week_days || [])];
                      const idx = days.indexOf(day.value);
                      if (idx > -1) days.splice(idx, 1);
                      else days.push(day.value);
                      localRule.week_days = days;
                    }"
                  >
                    {{ day.full }}
                  </button>
                </div>
              </div>

              <!-- Monthly options -->
              <div v-if="localRule.frequency === 'monthly'" class="space-y-4">
                <label class="block text-sm font-medium text-n-text dark:text-n-text-white">
                  {{ t('RECURRENCE.MONTHLY_TYPE.LABEL') }}
                </label>
                <div class="flex gap-4">
                  <button
                    v-for="opt in monthlyTypeOptions"
                    :key="opt.value"
                    type="button"
                    class="flex-1 px-4 py-3 border-2 rounded-lg text-center transition-all"
                    :class="{
                      'border-n-primary-500 bg-n-primary-50 dark:bg-n-primary-900/20 text-n-primary-700 dark:text-n-primary-300':
                        localRule.monthly_type === opt.value,
                      'border-n-divider dark:border-n-divider text-n-text dark:text-n-text-white hover:border-n-primary-300':
                        localRule.monthly_type !== opt.value,
                    }"
                    @click="setMonthlyType(opt.value)"
                  >
                    {{ opt.label }}
                  </button>
                </div>

                <div v-if="localRule.monthly_type === 'day_of_month'" class="space-y-2">
                  <label class="block text-sm font-medium text-n-text dark:text-n-text-white">
                    {{ t('RECURRENCE.DAY_OF_MONTH') }}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    v-model.number="localRule.month_day"
                    class="w-32 px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
                  />
                </div>

                <div v-if="localRule.monthly_type === 'day_of_week'" class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-n-text dark:text-n-text-white mb-1">
                      {{ t('RECURRENCE.WEEK_OF_MONTH') }}
                    </label>
                    <select
                      v-model.number="localRule.monthly_week"
                      class="w-full px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
                    >
                      <option value="1">{{ t('RECURRENCE.WEEKS.FIRST') }}</option>
                      <option value="2">{{ t('RECURRENCE.WEEKS.SECOND') }}</option>
                      <option value="3">{{ t('RECURRENCE.WEEKS.THIRD') }}</option>
                      <option value="4">{{ t('RECURRENCE.WEEKS.FOURTH') }}</option>
                      <option value="-1">{{ t('RECURRENCE.WEEKS.LAST') }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-n-text dark:text-n-text-white mb-1">
                      {{ t('RECURRENCE.WEEKDAY') }}
                    </label>
                    <select
                      v-model.number="localRule.monthly_weekday"
                      class="w-full px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
                    >
                      <option v-for="d in 7" :key="d-1" :value="d-1">{{ fullDayName(d-1) }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Yearly options -->
              <div v-if="localRule.frequency === 'yearly'" class="space-y-4 grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-n-text dark:text-n-text-white mb-1">
                    {{ t('RECURRENCE.MONTH') }}
                  </label>
                  <select
                    v-model.number="localRule.year_month"
                    class="w-full px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
                  >
                    <option v-for="m in 12" :key="m" :value="m">{{ monthName(m-1) }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-n-text dark:text-n-text-white mb-1">
                    {{ t('RECURRENCE.DAY') }}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    v-model.number="localRule.year_day"
                    class="w-full px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <!-- End Tab -->
            <div v-if="activeTab === 'end'" class="space-y-6">
              <div>
                <label class="block text-sm font-medium text-n-text dark:text-n-text-white mb-3">
                  {{ t('RECURRENCE.END') }}
                </label>
                <div class="space-y-3">
                  <label
                    v-for="opt in endTypeOptions"
                    :key="opt.value"
                    class="flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all"
                    :class="{
                      'border-n-primary-500 bg-n-primary-50 dark:bg-n-primary-900/20': localRule.end_type === opt.value,
                      'border-n-divider dark:border-n-divider hover:border-n-primary-300': localRule.end_type !== opt.value,
                    }"
                    @click="setEndType(opt.value)"
                  >
                    <input
                      type="radio"
                      :value="opt.value"
                      :checked="localRule.end_type === opt.value"
                      class="w-4 h-4 text-n-primary-600"
                    />
                    <span class="text-n-text dark:text-n-text-white">{{ opt.label }}</span>
                  </label>
                </div>
              </div>

              <div v-if="localRule.end_type === 'on_date'" class="space-y-2">
                <label class="block text-sm font-medium text-n-text dark:text-n-text-white">
                  {{ t('RECURRENCE.END_DATE') }}
                </label>
                <input
                  type="date"
                  v-model="localRule.end_date"
                  class="w-full max-w-xs px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
                />
              </div>

              <div v-if="localRule.end_type === 'after_count'" class="space-y-2">
                <label class="block text-sm font-medium text-n-text dark:text-n-text-white">
                  {{ t('RECURRENCE.OCCURRENCES') }}
                </label>
                <input
                  type="number"
                  min="1"
                  v-model.number="localRule.end_count"
                  class="w-32 px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex justify-end gap-3 border-t border-n-divider dark:border-white/10 px-6 py-4">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-n-text dark:text-n-text-white border border-n-divider rounded-lg hover:bg-n-surface-1 dark:hover:bg-n-surface-3 transition-colors"
              @click="close"
            >
              {{ t('SCHEDULED_MESSAGES.DISCARD') }}
            </button>
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-white bg-n-primary-600 rounded-lg hover:bg-n-primary-700 transition-colors"
              @click="save"
              :disabled="!localRule.frequency || (localRule.frequency === 'weekly' && !localRule.week_days?.length)"
            >
              {{ t('SCHEDULED_MESSAGES.SAVE') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>